import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
import Button from "./button";
import { useLanguage } from "../../context/LanguageContext";
import DOMPurify from "dompurify";
import phonepayDefault from "../../assets/images/backgrounds/phonepay.png";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(({ className, zIndex = "z-50", ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      `fixed inset-0 ${zIndex} bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out`,
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

const DialogContent = React.forwardRef(
  ({ className, children, showHeaderImage, headerImage, zIndex = "z-50", ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay zIndex={zIndex} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          `fixed left-1/2 top-1/2 ${zIndex} w-full max-w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4 pt-8 shadow-xl outline-none ring-1 ring-black/5`,
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          className
        )}
        {...props}
      >
        {/* Floating circular illustration centered above the card content */}
        {showHeaderImage && (
          <div className="">
            <div className="mx-auto mb-0 flex items-center justify-center">
              {(() => {
                try {
                  // If headerImage is already imported, use it directly
                  if (
                    typeof headerImage === "string" &&
                    !headerImage.startsWith("../../")
                  ) {
                    return (
                      <img
                        src={headerImage}
                        className="w-[100px]"
                        alt=""
                        aria-hidden
                      />
                    );
                  }
                  // Otherwise, try dynamic require for path strings
                  // eslint-disable-next-line global-require, import/no-dynamic-require
                  const imageSrc = require(`${headerImage}`);
                  return (
                    <img src={imageSrc} className="h-40" alt="" aria-hidden />
                  );
                } catch (error) {
                  // Fallback to default phonepay image
                  try {
                    const fallbackImage = require("../../assets/images/backgrounds/phonepay.png");
                    return (
                      <img
                        src={fallbackImage}
                        className="w-[100px]"
                        alt=""
                        aria-hidden
                      />
                    );
                  } catch {
                    return (
                      <div className="relative flex items-center justify-center w-12 h-12">
                        <div className="absolute -left-1 -top-1 w-11 h-8 rounded-full bg-blue-500" />
                        <div className="absolute left-2 top-3 w-8 h-5 rounded bg-white shadow-sm border border-white/30" />
                      </div>
                    );
                  }
                }
              })()}
            </div>
          </div>
        )}

        {/* Close button (small white circle) */}
        <DialogPrimitive.Close
          className="absolute right-3 top-3 inline-flex items-center justify-center rounded-full bg-white p-1.5 text-gray-600 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>

        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = "DialogContent";

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn("mt-4 flex flex-col items-center gap-3", className)}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-md font-bold leading-6 tracking-tight text-theme-primary",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm font-normal text-theme-secondary mt-4", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

/**
 * Generic Modal
 *
 * Props:
 * - open (bool)
 * - onClose (fn)
 * - children (ReactNode) -> if provided, rendered inside the modal content
 * - title (string | ReactNode) optional quick header
 * - description (string | ReactNode) optional description below title
 * - footer (ReactNode) optional footer area; if not provided and no children, falls back to previous buttons
 * - headerImage (string) optional path to header image, defaults to phonepay.png
 * - showHeaderImage (bool) optional show/hide header image, defaults to true
 * - type (string) optional modal type: 'success' | 'error' | 'info' | 'confirm' | 'custom'
 * - message (string | ReactNode) optional message for predefined types
 * - onConfirm (fn) optional callback for confirmation modals
 * - confirmText (string) optional text for confirm button, defaults to "Confirm"
 * - cancelText (string) optional text for cancel button, defaults to "Cancel"
 * - onLinkCardClick (fn) optional callback for "Link Card" button click in default modal
 * - renderAsHtml (bool) optional flag to render message as HTML, defaults to false
 *
 * Backwards compatible: when no children are passed, the previous default content is used.
 */
export default function Modal({
  open = false,
  onClose = () => {},
  children = null,
  title = null,
  description = null,
  footer = null,
  headerImage = phonepayDefault,
  showHeaderImage = true,
  type = null,
  message = null,
  onConfirm = null,
  confirmText = null,
  cancelText = null,
  buttonText = null,
  onLinkCardClick = null,
  zIndex = null,
  tenanturlname = null,
  renderAsHtml = false,
  showHowToRegister = true,
}) {
  const { t } = useLanguage();
  const hasCustomContent = Boolean(children);
  const hasHeader = Boolean(title || description);
  const hasType = Boolean(type);

  // Use higher z-index for status modals (error/success/info) to appear above regular modals
  const modalZIndex = zIndex || (type === 'error' || type === 'success' || type === 'info' ? 'z-[100]' : 'z-50');

  // Use translations as defaults
  const actualConfirmText = confirmText || t("common.confirm");
  const actualCancelText = cancelText || t("common.cancel");
  const actualButtonText = buttonText || t("common.ok");

  // Icon rendering based on type
  const renderTypeIcon = () => {
    if (!hasType) return null;

    switch (type) {
      case "success":
        return (
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              className="text-green-600"
            >
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      case "error":
        return (
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              className="text-red-600"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      case "info":
        return (
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              className="text-blue-600"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 16v-4M12 8h.01"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        );
      case "confirm":
        return (
          <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              className="text-yellow-600"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 9v4M12 17h.01"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Modal Welcome
  return (
    // Only invoke onClose when the dialog is closed. Radix calls onOpenChange on both open and close;
    // calling onClose directly caused the modal to immediately close when opening.
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        showHeaderImage={!hasType && showHeaderImage}
        headerImage={headerImage}
        zIndex={modalZIndex}
      >
        {/* center the modal's inner content */}
        <div className="flex flex-col items-center justify-center text-center">
          {hasCustomContent ? (
            // Render whatever consumer passes
            children
          ) : hasType ? (
            // Render type-based modal (success/error/info/confirm)
            <>
              {renderTypeIcon()}

              <DialogTitle
                className={cn(
                  type === "success" && "text-green-600",
                  type === "error" && "text-red-600",
                  type === "info" && "text-blue-600",
                  type === "confirm" && "text-yellow-700"
                )}
              >
                {title ||
                  (type === "success"
                    ? t("common.success")
                    : type === "error"
                    ? t("common.error")
                    : type === "info"
                    ? t("common.information")
                    : t("common.confirm"))}
              </DialogTitle>

              {(message || description) && (
                <DialogDescription
                  className={cn(
                    "max-w-[280px] mx-auto mt-2",
                    !renderAsHtml && "whitespace-pre-line",
                  )}
                >
                  {renderAsHtml ? (
                    <div 
                      className="text-left"
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(message || description) 
                      }} 
                    />
                  ) : (
                    message || description
                  )}
                </DialogDescription>
              )}

              <DialogFooter>
                {type === "confirm" ? (
                  <div className="w-full flex flex-col gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => {
                        if (onConfirm) onConfirm();
                        onClose();
                      }}
                      className="w-full"
                    >
                      {actualConfirmText}
                    </Button>
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={onClose}
                      className="w-full"
                    >
                      {actualCancelText}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={onClose}
                    className="min-w-[122px]"
                  >
                    {actualButtonText}
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : (
            // Backwards-compatible default layout (or title/description/optional footer)
            <>
              {hasHeader && (
                <DialogHeader>
                  {title && <DialogTitle>{title}</DialogTitle>}
                  {description && (
                    <DialogDescription className="max-w-[280px] mx-auto">
                      {description}
                    </DialogDescription>
                  )}
                </DialogHeader>
              )}

              {/* If no header/children, show welcome message with image */}
              {!hasHeader && (
                <>
                  <DialogHeader>
                    <DialogTitle>{t("welcomeModal.title")}</DialogTitle>
                    <DialogDescription className="max-w-[280px] mx-auto">
                      {t("welcomeModal.description")}
                    </DialogDescription>
                  </DialogHeader>
                </>
              )}

              <DialogFooter>
                {footer ? (
                  footer
                ) : (
                  <div className="w-full flex flex-col items-center">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => {
                        if (onLinkCardClick) {
                          onLinkCardClick();
                        }
                        onClose();
                      }}
                      className="max-w-[122px] "
                    >
                      {t("welcomeModal.linkCardButton")}
                    </Button>
                    {showHowToRegister && (
                      <a
                        href={`/${tenanturlname}/about`}
                        className="mt-4 block text-center text-sm font-semibold text-theme-primary hover:underline"
                        onClick={() => onClose()}
                      >
                        {t("welcomeModal.aboutVoxLink")}
                      </a>
                    )}
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
